import React, { useState, useEffect, useContext, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import * as yup from 'yup';
import { useFormik } from 'formik';
import Modal from 'react-bootstrap/Modal';
import 'bootstrap/dist/css/bootstrap.min.css';
import classNames from 'classnames';
import {
  getTimezoneDate,
  getTimezoneTime,
  setTimezoneDate,
  setTimezoneTime,
} from '../../helpers/timezones';
import UserService from '../../services/UserService';
import AdminService from '../../services/AdminService';
import { UiContext } from '../../context/UiContext';
import classes from './styles.module.scss';
import { hasAuthData } from '../../helpers/authStorage';

import EventPopupButtons from '../../components/Dashboard/EventPopupButtons';
import ChangeRequestHeader from '../../components/Dashboard/ChangeRequestHeader';
import TimeRange from '../../components/Form/TimeRange';
import SelectedUsers from '../../components/Dashboard/SelectedUsers';
import Textarea from '../../components/Form/Textarea';
import MultiDropdown from '../../components/Form/MultiDropdown';
import FormDropdown from '../../components/Form/FormDropdown';
import DatePicker from '../../components/Form/DatePicker';

import yupSchemaToFields from '../../helpers/yupSchemaToFields';
import { startTimeNotInPastIfToday } from '../../helpers/validation';

export default function SetJoiningModal({
  show,
  handleClose,
  candidate,
  refetch,
}) {
  const [isTriedToSubmit, setIsTriedToSubmit] = useState(false);
  const [isEditChangeRequestMode, setIsEditChangeRequestMode] = useState(false);

  const { showNotification, setIsFetching, showModal } = useContext(UiContext);

  const { t } = useTranslation();

  const isAuthorized = hasAuthData();

  const isEdit =
    candidate &&
    candidate.jobApplicationJoinings?.length &&
    candidate.jobApplicationJoinings.some((j) => j.status !== 'Canceled');

  console.log('candidate', candidate.jobApplicationJoinings);

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: UserService.getMe,
    enabled: isAuthorized,
  });

  const closeAndNotify = (message) => {
    handleClose();
    refetch();
    showNotification({
      text: message,
    });
  };

  let currentJoining = candidate?.jobApplicationJoinings?.at(-1);

  if (currentJoining?.status === 'Canceled') {
    currentJoining = null;
  }

  const isChangeRequested = currentJoining?.status === 'ChangeRequest';

  const validationSchema = useMemo(
    () =>
      yup.object({
        joiningDate: yup
          .date()
          .required(t('modals.SetJoiningModal.requiredField')),
        location: yup.object(),
        userIds: yup
          .array()
          .required(t('modals.SetJoiningModal.requiredField')),
        note: yup.string().trim(),
        startTime: yup
          .string()
          .trim()
          .required(t('common.startTimeRequired'))
          .test(
            'not-in-past-if-today',
            t('modals.ScheduleInterviewModal.startTimeGreaterThanCurrentTime'),
            startTimeNotInPastIfToday('joiningDate')
          ),
        endTime: yup
          .string()
          .trim()
          .required(t('common.endTimeRequired'))
          .test(
            'less-than-start',
            t('common.endTimeGreaterThanStartTime'),
            (value, ctx) => {
              if (!ctx.parent.startTime) {
                return true;
              }
              const endTime = parseInt(value?.replace(':', ''), 10);
              const startTime = parseInt(
                ctx.parent.startTime?.replace(':', ''),
                10
              );
              return !(endTime <= startTime);
            }
          ),
      }),
    [t]
  );

  const formik = useFormik({
    initialValues: {
      joiningDate: isEdit
        ? getTimezoneDate(
            currentJoining?.joiningDate,
            me?.userProfile?.timezone,
            currentJoining?.startTime
          )[0]
        : '',
      location: isEdit
        ? {
            value: currentJoining?.location?.id,
            label: currentJoining?.location?.name,
          }
        : '',
      userIds: isEdit
        ? currentJoining?.users?.map((user) => ({
            label: user.userName,
            value: user.id,
          }))
        : '',
      note: isEdit ? currentJoining?.note : '',
      startTime: isEdit
        ? getTimezoneTime(currentJoining?.startTime, me?.userProfile?.timezone)
        : '',
      endTime: isEdit
        ? getTimezoneTime(currentJoining?.endTime, me?.userProfile?.timezone)
        : '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsFetching(true);

        /*      if (isEdit) {
          await AdminService.deleteJobApplicationStartDate({
            jobApplicationId: candidate.id,
            joiningid: candidate.jobApplicationJoinings[0].id,
          });
        } */

        const requestHandler = isEdit
          ? AdminService.changeJobApplicationStartDate
          : AdminService.createJobApplicationStartDate;

        await requestHandler({
          joiningId: candidate?.jobApplicationJoinings?.[0]?.id,
          joiningDate: setTimezoneDate(
            values.joiningDate,
            values.startTime,
            me?.userProfile?.timezone
          ),
          locationName: values.location.label,
          locationId: values.location.value,
          userIds: values.userIds.map((user) => user.value),
          note: values.note,
          startTime: setTimezoneTime(
            values.startTime,
            me?.userProfile?.timezone
          ),
          endTime: setTimezoneTime(values.endTime, me?.userProfile?.timezone),
          jobApplicationId: candidate.id,
          status: isEdit ? 'Rescheduled' : 'Scheduled',
        });

        handleClose();
        await refetch();
        closeAndNotify(
          isEdit
            ? t('modals.SetJoiningModal.startDateUpdated')
            : t('modals.SetJoiningModal.startDateSent')
        );
      } catch (error) {
        console.log(error);
      } finally {
        setIsFetching(false);
      }
    },
    enableReinitialize: true,
  });

  console.log('currentJoining', isEdit);

  const deleteStartDate = async () => {
    showModal({
      title: t('modals.SetJoiningModal.cancelStartDate'),
      text: t('modals.SetJoiningModal.cancelStartDateText'),
      dismissButtonLabel: t('common.no'),
      confirmButtonLabel: t('common.yes'),
      onConfirm: async () => {
        try {
          setIsFetching(true);
          await AdminService.deleteJobApplicationStartDate({
            jobApplicationId: candidate.id,
            joiningid: candidate.jobApplicationJoinings[0].id,
          });
          closeAndNotify(t('modals.SetJoiningModal.canceled'));
        } catch (error) {
          console.log(error);
        } finally {
          setIsFetching(false);
        }
      },
    });
  };

  useEffect(() => {
    return () => {
      formik.resetForm();
      setIsTriedToSubmit(false);
      setIsEditChangeRequestMode(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const isDisabled =
    Object.keys(formik.values).some(
      (key) => !formik.values[key] && key !== 'note'
    ) ||
    !formik.dirty ||
    formik.isSubmitting;

  const isReadOnly = isChangeRequested && !isEditChangeRequestMode;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      enforceFocus={false}
      className={classes.SetJoiningModal}
      backdropClassName={classes.backdrop}
      contentClassName={classes.modalContent}
      dialogClassName={classes.dialog}
    >
      <div className={classes.content}>
        <header>
          {isChangeRequested ? (
            <ChangeRequestHeader
              title={t('modals.SetJoiningModal.newJoiner')}
              suggestedDate={currentJoining?.suggestedJoiningDate}
              suggestedStartTime={currentJoining?.suggestedStartTime}
            />
          ) : (
            <h1 className={classes.title}>
              {t('modals.SetJoiningModal.setStartDate')}
              {(candidate?.jobApplicationJoinings?.[0]?.status === 'Accepted' ||
                candidate?.jobApplicationJoinings?.[0]?.status ===
                  'Declined') && (
                <div
                  className={classNames(classes.status, {
                    [classes.statusDeclined]:
                      candidate?.jobApplicationJoinings?.[0]?.status ===
                      'Declined',
                  })}
                >
                  {candidate?.jobApplicationJoinings?.[0]?.status}
                </div>
              )}
            </h1>
          )}
          <i className={classes.closeIcon} onClick={handleClose}>
            Close
          </i>
        </header>
        <div className={classes.container}>
          <div className={classes.row}>
            <div className={classes.col}>
              <DatePicker
                readonly={isReadOnly}
                dateFieldWidth={350}
                label={t('modals.SetJoiningModal.startDate')}
                name="joiningDate"
                value={formik.values.joiningDate}
                setFieldValue={formik.setFieldValue}
                error={formik.errors.joiningDate}
                setFieldTouched={formik.setFieldTouched}
              />
            </div>
            <div className={classes.col}>
              <TimeRange
                readOnly={isReadOnly}
                fromValue={formik.values.startTime}
                toValue={formik.values.endTime}
                setFieldValue={formik.setFieldValue}
                fromName="startTime"
                toName="endTime"
                error={formik.errors.endTime}
                startTimeError={formik.errors.startTime}
                touched={isTriedToSubmit}
                label={t('modals.SetJoiningModal.time')}
              />
            </div>
          </div>
          <div className={classes.row}>
            <div className={classes.col}>
              <FormDropdown
                readOnly={isReadOnly}
                height={50}
                width={350}
                label={t('modals.SetJoiningModal.location')}
                placeholder={t('modals.SetJoiningModal.selectLocation')}
                fetchOptions={{
                  dataName: 'restaurantsOptionsBasicInfo',
                  getDataHandler: AdminService.getRestaurants,
                  fetchHanlderOptions: {
                    Filters: candidate?.jobPost?.company?.id
                      ? `company.id:eq:${candidate?.jobPost?.company?.id}`
                      : null,
                  },
                }}
                value={formik.values.location}
                infoWidth={151}
                name="location"
                searchInfo={t('modals.SetJoiningModal.locationSearchInfo')}
                searchInfoWidth={188}
                setFieldValue={formik.setFieldValue}
              />
            </div>
            <div className={classes.col}>
              <MultiDropdown
                readOnly={isReadOnly}
                height={50}
                maxLength={3}
                note={t('modals.SetJoiningModal.selectUpTo3')}
                width={350}
                label={t('modals.SetJoiningModal.personToMeet')}
                fetchOptions={{
                  dataName: 'userOptionPostJobSuccess',
                  getDataHandler: AdminService.getUsers,
                  labelName: 'userName',
                  isUserList: true,
                }}
                value={formik.values.userIds}
                name="userIds"
                setFieldValue={formik.setFieldValue}
                placeholder={t('modals.SetJoiningModal.selectName')}
              />

              <div className={classes.selectedUsers}>
                <SelectedUsers
                  readOnly={isReadOnly}
                  invitedUsers={formik.values.userIds}
                  withFormik
                  setInvitedUsers={(users) =>
                    formik.setFieldValue('userIds', users)
                  }
                />
              </div>
            </div>
          </div>
          <div className={classes.row}>
            <div className={classes.col}>
              <Textarea
                readonly={isReadOnly}
                name="note"
                onChange={formik.handleChange}
                value={formik.values.note}
                label={t('modals.SetJoiningModal.emailMessage')}
                note={t('modals.SetJoiningModal.optional')}
                height={145}
                placeholder={t('modals.SetJoiningModal.enterNotes')}
                // maxLength={5000}
              />
            </div>
          </div>
        </div>
        <footer>
          <EventPopupButtons
            isEdit={isEdit}
            deleteEvent={deleteStartDate}
            formik={formik}
            setIsTriedToSubmit={setIsTriedToSubmit}
            isDisabled={isDisabled}
            isChangeRequested={isChangeRequested}
            isEditChangeRequestMode={isEditChangeRequestMode}
            setIsEditChangeRequestMode={setIsEditChangeRequestMode}
            fields={yupSchemaToFields(validationSchema)}
          />
        </footer>
      </div>
    </Modal>
  );
}
